// Normalization stage (spec s4): N remote WebRTC tracks -> one 16 kHz mono
// Int16 PCM stream. Shared by every AudioSource that starts from
// MediaStreamTracks, so LiveSwitch and the dev sources use identical plumbing.

import { FRAME_SAMPLES, SAMPLE_RATE, type FrameHandler } from './AudioSource';
import { debugLog, describe } from '../debug';

// Served as a real file from /public rather than bundled. A small bundled asset
// gets inlined as a data: URL, and addModule() on a data: URL is not reliably
// supported. Deploying this app means deploying this file alongside it.
const WORKLET_URL = `${import.meta.env.BASE_URL}pcm-worklet.js`;

type TrackEntry = { node: MediaStreamAudioSourceNode; stream: MediaStream };

export class MonoMixerGraph {
  private ctx: AudioContext | null = null;
  private mixer: GainNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private sink: GainNode | null = null;
  private tracks = new Map<string, TrackEntry>();
  private frameHandler: FrameHandler | null = null;

  onFrame(handler: FrameHandler): void {
    this.frameHandler = handler;
  }

  get trackCount(): number {
    return this.tracks.size;
  }

  async start(): Promise<void> {
    if (this.ctx) return;

    // Ask for 16 kHz directly and let the browser resample the 48 kHz WebRTC
    // tracks. Hand-rolled resampling is a bug farm for no gain.
    const ctx = new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: 'interactive' });
    if (ctx.state === 'suspended') await ctx.resume();
    // A context stuck in 'suspended' produces no frames at all, and a rate that
    // is not what we asked for means the worklet's maths is wrong.
    debugLog.info('mixer: context', `state=${ctx.state}`, `rate=${ctx.sampleRate}`);

    try {
      await ctx.audioWorklet.addModule(WORKLET_URL);
    } catch (err) {
      // Worth naming: if this 404s, CloudFront serves index.html with a 200 and
      // the failure reads as a syntax error in a file that looks fine.
      debugLog.error('mixer: worklet failed to load from', WORKLET_URL, describe(err));
      throw err;
    }

    // Everything sums into one explicitly-mono node. Multiple connections into
    // a single input sum; forcing channelCount 1 does the downmix.
    const mixer = ctx.createGain();
    mixer.gain.value = 1;
    mixer.channelCount = 1;
    mixer.channelCountMode = 'explicit';
    mixer.channelInterpretation = 'speakers';

    const worklet = new AudioWorkletNode(ctx, 'pcm-frame-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: 'explicit',
    });
    worklet.port.onmessage = (event: MessageEvent<{ pcm: Int16Array; timestampMs: number }>) => {
      const { pcm, timestampMs } = event.data;
      this.frameHandler?.(pcm, timestampMs);
      // The frame's buffer was transferred in; dropping the reference here is
      // what keeps a 60-minute call flat (spec s12).
    };

    // A muted sink keeps the graph pulling even with no tracks attached, so the
    // worklet emits silence frames instead of the stream going quiet (spec s4).
    // It must reach the destination to be pulled; gain 0 means nothing is heard,
    // which also rules out any echo path back into the call.
    const sink = ctx.createGain();
    sink.gain.value = 0;

    mixer.connect(worklet);
    worklet.connect(sink);
    sink.connect(ctx.destination);

    this.ctx = ctx;
    this.mixer = mixer;
    this.worklet = worklet;
    this.sink = sink;
  }

  addTrack(id: string, track: MediaStreamTrack): void {
    if (!this.ctx || !this.mixer) throw new Error('MonoMixerGraph.start() must run first');
    if (this.tracks.has(id)) return;
    if (track.kind !== 'audio') return;

    const stream = new MediaStream([track]);
    const node = this.ctx.createMediaStreamSource(stream);
    node.connect(this.mixer);
    this.tracks.set(id, { node, stream });
    debugLog.info('mixer: now mixing', this.tracks.size, 'track(s)');
  }

  removeTrack(id: string): void {
    const entry = this.tracks.get(id);
    if (!entry) return;
    entry.node.disconnect();
    this.tracks.delete(id);
  }

  /** Push synthetic samples through the same conversion path (dev sources). */
  createBufferSource(): AudioBufferSourceNode {
    if (!this.ctx || !this.mixer) throw new Error('MonoMixerGraph.start() must run first');
    const source = this.ctx.createBufferSource();
    source.connect(this.mixer);
    return source;
  }

  get context(): AudioContext {
    if (!this.ctx) throw new Error('MonoMixerGraph.start() must run first');
    return this.ctx;
  }

  async stop(): Promise<void> {
    for (const id of [...this.tracks.keys()]) this.removeTrack(id);
    this.worklet?.port.postMessage('stop');
    if (this.worklet) this.worklet.port.onmessage = null;
    this.worklet?.disconnect();
    this.mixer?.disconnect();
    this.sink?.disconnect();
    await this.ctx?.close();
    this.ctx = null;
    this.mixer = null;
    this.worklet = null;
    this.sink = null;
    this.frameHandler = null;
  }
}

export { FRAME_SAMPLES, SAMPLE_RATE };
